import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fixedHorizonOptionScorecardActivationWarning } from '@einhaus/project314-shared';
import { trpc } from '@/api/trpc.ts';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { Button, Card, ConfirmationDialog, EmptyState } from '@/components/UI';

interface EntryScorecardCardProps {
    enabled: boolean;
    isEditMode: boolean;
    isOwnedStrategy: boolean;
    strategyId: number | null;
}

const EntryScorecardCard = ({ enabled, isEditMode, isOwnedStrategy, strategyId }: EntryScorecardCardProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const scorecardsQuery = useQuery({
        ...trpc.scorecards.list.queryOptions(),
        enabled: enabled && !!strategyId && isOwnedStrategy
    });
    const strategyQuery = useQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: strategyId ? [strategyId] : [] }),
        enabled: enabled && !!strategyId && isOwnedStrategy
    });

    const invalidateAttachment = () => {
        if (!strategyId) return;
        void queryClient.invalidateQueries({
            queryKey: trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [strategyId] }).queryKey
        });
        void queryClient.invalidateQueries({
            queryKey: trpc.backtest.getStrategyScorecardContext.queryOptions({ strategyId }).queryKey
        });
        void queryClient.invalidateQueries({ queryKey: trpc.scorecards.list.queryOptions().queryKey });
    };

    const attachScorecardMutation = useTrpcMutation(trpc.scorecards.attach.mutationOptions({ onSuccess: invalidateAttachment }), {
        successMessage: 'Scorecard version attached for testing',
        fallbackErrorMessage: 'Failed to attach scorecard'
    });
    const detachScorecardMutation = useTrpcMutation(trpc.scorecards.detach.mutationOptions({ onSuccess: invalidateAttachment }), {
        successMessage: 'Scorecard detached',
        fallbackErrorMessage: 'Failed to detach scorecard'
    });

    const strategy = strategyQuery.data?.strategies[0] ?? null;
    const scorecards = scorecardsQuery.data ?? [];
    const attachedScorecard = scorecards.find((scorecard) => scorecard.id === strategy?.activeScorecardId) ?? null;
    const availableScorecards = scorecards.filter(
        (scorecard) => scorecard.initializationStatus === 'ready' && scorecard.id !== attachedScorecard?.id
    );

    // Fixed-horizon option scorecards need an explicit acknowledgment before attaching; the dialog holds
    // the scorecard awaiting that acknowledgment.
    const [pendingAcknowledgmentScorecardId, setPendingAcknowledgmentScorecardId] = useState<number | null>(null);
    const pendingAcknowledgmentScorecard = scorecards.find((scorecard) => scorecard.id === pendingAcknowledgmentScorecardId) ?? null;
    const pendingActivationWarning = pendingAcknowledgmentScorecard
        ? fixedHorizonOptionScorecardActivationWarning(pendingAcknowledgmentScorecard)
        : null;

    const attachScorecardVersion = async (scorecardId: number, acknowledgeFixedHorizonProxy: boolean) => {
        if (!strategyId) throw new Error('A strategy is required to attach a scorecard');

        await attachScorecardMutation.mutateAsync({ strategyId, scorecardId, acknowledgeFixedHorizonProxy });
    };

    return (
        <Card
            headerActions={
                isEditMode && strategyId && isOwnedStrategy ? (
                    <Button size="sm" variant="outline-dark" onClick={() => navigate(`/user/scorecards?strategyId=${strategyId}`)}>
                        Manage scorecards
                    </Button>
                ) : undefined
            }
            subtitle="Attach a locked version, or test an editable ready draft to lock and attach its exact settings."
            title="Entry scorecard"
        >
            {!isEditMode || !strategyId ? (
                <div className="text-zinc-400 text-sm">Save the strategy first to manage scorecards.</div>
            ) : !isOwnedStrategy ? (
                <div className="text-zinc-400 text-sm">Scorecard management remains owner-only for shared strategies.</div>
            ) : scorecardsQuery.isLoading || strategyQuery.isLoading ? (
                <div className="text-zinc-400 text-sm">Loading scorecards…</div>
            ) : scorecardsQuery.isError || strategyQuery.isError ? (
                <div className="text-red-400 text-sm">Failed to load scorecards.</div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <div className="text-zinc-300 font-medium mb-2">Attached</div>
                        {!attachedScorecard ? (
                            <EmptyState size="sm" title="No scorecard attached" />
                        ) : (
                            <div className="flex items-center justify-between bg-zinc-900 rounded p-2">
                                <Link
                                    className="text-primary hover:underline"
                                    to={`/user/scorecard/${attachedScorecard.id}?strategyId=${strategyId}`}
                                >
                                    {attachedScorecard.name}
                                </Link>
                                <Button size="sm" variant="secondary" onClick={() => detachScorecardMutation.mutate({ strategyId })}>
                                    {detachScorecardMutation.isPending ? 'Detaching…' : 'Detach'}
                                </Button>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-zinc-300 font-medium mb-2">Ready scorecards</div>
                        {availableScorecards.length === 0 ? (
                            <EmptyState size="sm" title="No other ready scorecards" />
                        ) : (
                            <div className="space-y-2">
                                {availableScorecards.map((scorecard) => (
                                    <div className="flex items-center justify-between bg-zinc-900 rounded p-2" key={scorecard.id}>
                                        <div>
                                            <Link
                                                className="text-primary hover:underline"
                                                to={`/user/scorecard/${scorecard.id}?strategyId=${strategyId}`}
                                            >
                                                {scorecard.name}
                                            </Link>
                                            <div className="text-xs text-zinc-500">
                                                {scorecard.lockedAt === null ? 'Editable draft' : 'Locked version'}
                                            </div>
                                        </div>
                                        {scorecard.lockedAt === null ? (
                                            <Button
                                                size="sm"
                                                to={`/user/scorecard/${scorecard.id}?strategyId=${strategyId}`}
                                                variant="primary"
                                            >
                                                Review &amp; Test
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={async () => {
                                                    if (fixedHorizonOptionScorecardActivationWarning(scorecard) !== null) {
                                                        setPendingAcknowledgmentScorecardId(scorecard.id);
                                                        return;
                                                    }

                                                    await attachScorecardVersion(scorecard.id, false);
                                                }}
                                            >
                                                {attachScorecardMutation.isPending ? 'Attaching…' : 'Attach Version'}
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmationDialog
                confirmText="Attach Version"
                confirmVariant="primary"
                isConfirmLoading={attachScorecardMutation.isPending}
                isOpen={pendingAcknowledgmentScorecard !== null}
                message={
                    <div className="space-y-3 text-sm">
                        {pendingAcknowledgmentScorecard && (
                            <p>
                                Attach <span className="font-medium text-white">{pendingAcknowledgmentScorecard.name}</span> to this
                                strategy?
                            </p>
                        )}
                        {pendingActivationWarning && (
                            <p className="rounded border border-amber-700/60 bg-amber-950/30 p-3 text-amber-100">
                                {pendingActivationWarning}
                            </p>
                        )}
                    </div>
                }
                title="Attach this scorecard version?"
                onCancel={() => setPendingAcknowledgmentScorecardId(null)}
                onConfirm={async () => {
                    if (!pendingAcknowledgmentScorecard) return;

                    try {
                        await attachScorecardVersion(pendingAcknowledgmentScorecard.id, pendingActivationWarning !== null);
                    } finally {
                        setPendingAcknowledgmentScorecardId(null);
                    }
                }}
            />
        </Card>
    );
};

export default EntryScorecardCard;
